enum Products {
  DAM = "DAM",
  RTM = "RTM",
  HPDAM = "HPDAM",
  GDAM = "GDAM",

  IntraDay = "Intra-Day Contracts",
  ContigencyContracts = "Day Ahead Contingency Contracts",
  Daily = "Daily Contracts",
  Weekly = "Weekly Contracts",
  Monthly = "Monthly Contracts",
  AnyDay = "Any Day Contracts",
  SingleSided = "Any Day Single Sided Contracts",
  Bilateral = "Trading Licensees",
}

export interface MarketMonitoringItem {
  month: string;
  product: Products;
  value: number;
  totalVolume?: number;
  totalCost?: number;
}

export interface MarketMonitoringData {
  dam: MarketMonitoringItem[];
  rtm: MarketMonitoringItem[];
  hpdam: MarketMonitoringItem[];
  gdam: MarketMonitoringItem[];
  intraDay: MarketMonitoringItem[];
  contingencyContracts: MarketMonitoringItem[];
  daily: MarketMonitoringItem[];
  weekly: MarketMonitoringItem[];
  monthly: MarketMonitoringItem[];
  anyDay: MarketMonitoringItem[];
  singleSided: MarketMonitoringItem[];
  bilateral: MarketMonitoringItem[];
}

export interface ChartExchangeItem {
  month: string;
  dam: number;
  rtm: number;
  hpdam: number;
  gdam: number;
  intraDay: number;
  contingencyContracts: number;
  daily: number;
  weekly: number;
  monthly: number;
  anyDay: number;
  singleSided: number;
  bilateral: number;
  [key: string]: number | string | null;
}

export function FormatMarketMonitoringData(
  data: any,
  includingExchange?: String[],
  includingMarket?: String[],
  isWeighted?: boolean,
): ChartExchangeItem[] {
  const formattedDataArray: MarketMonitoringData = {
    dam: [],
    rtm: [],
    hpdam: [],
    gdam: [],
    intraDay: [],
    contingencyContracts: [],
    daily: [],
    weekly: [],
    monthly: [],
    anyDay: [],
    singleSided: [],
    bilateral: [],
  };

  const updateFormattedData = (
    dataArray: MarketMonitoringItem[],
    product: Products,
    item: any,
  ) => {
    const index = dataArray.findIndex(
      (element) => element.month === item.month,
    );

    if (index === -1) {
      var toPush: MarketMonitoringItem = {
        month: item.month,
        product: product,
        value: item.value,
      };
      if (isWeighted) {
        toPush.totalVolume = item.volume;
        toPush.totalCost = item.value * item.volume;
      }

      dataArray.push(toPush);
    } else {
      if (isWeighted) {
        dataArray[index].totalVolume += item.volume;
        dataArray[index].totalCost! += item.value * item.volume;
        dataArray[index].value =
          dataArray[index].totalVolume === 0
            ? 0
            : dataArray[index].totalCost! / dataArray[index].totalVolume!;
      } else {
        dataArray[index].value += item.value;
      }
    }
  };
  Object.keys(data).forEach((key) => {
    if (includingExchange?.includes(key)) {
      Object.keys(data[key]).forEach((key2) => {
        if (includingMarket?.includes(key2)) {
          data[key][key2].forEach((item: any) => {
            switch (item.product) {
              case Products.DAM:
                updateFormattedData(formattedDataArray.dam, Products.DAM, item);
                break;
              case Products.RTM:
                updateFormattedData(formattedDataArray.rtm, Products.RTM, item);
                break;
              case Products.HPDAM:
                updateFormattedData(
                  formattedDataArray.hpdam,
                  Products.HPDAM,
                  item,
                );
                break;
              case Products.GDAM:
                updateFormattedData(
                  formattedDataArray.gdam,
                  Products.GDAM,
                  item,
                );
                break;
              case Products.IntraDay:
                updateFormattedData(
                  formattedDataArray.intraDay,
                  Products.IntraDay,
                  item,
                );
                break;
              case Products.ContigencyContracts:
                updateFormattedData(
                  formattedDataArray.contingencyContracts,
                  Products.ContigencyContracts,
                  item,
                );
                break;
              case Products.Daily:
                updateFormattedData(
                  formattedDataArray.daily,
                  Products.Daily,
                  item,
                );
                break;
              case Products.Weekly:
                updateFormattedData(
                  formattedDataArray.weekly,
                  Products.Weekly,
                  item,
                );
                break;
              case Products.Monthly:
                updateFormattedData(
                  formattedDataArray.monthly,
                  Products.Monthly,
                  item,
                );
                break;
              case Products.AnyDay:
                updateFormattedData(
                  formattedDataArray.anyDay,
                  Products.AnyDay,
                  item,
                );
                break;
              case Products.SingleSided:
                updateFormattedData(
                  formattedDataArray.singleSided,
                  Products.SingleSided,
                  item,
                );
                break;
              case Products.Bilateral:
                updateFormattedData(
                  formattedDataArray.bilateral,
                  Products.Bilateral,
                  item,
                );
                break;
              case "Trading Licensees":
                updateFormattedData(
                  formattedDataArray.bilateral,
                  Products.Bilateral,
                  item,
                );
                break;

              default:
                break;
            }
          });
        } else {
        }
      });
    }
  });
  const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const parseMonthOrder = (m: string) => {
    const [mon, yr] = m.split("-");
    return parseInt(yr) * 12 + MONTHS.indexOf(mon);
  };

  // Collect all unique months across every product array and sort chronologically
  const allMonths = Array.from(
    new Set(
      (
        Object.keys(formattedDataArray) as (keyof MarketMonitoringData)[]
      ).flatMap((key) => formattedDataArray[key].map((item) => item.month)),
    ),
  ).sort((a, b) => parseMonthOrder(a) - parseMonthOrder(b));

  // Build lookup maps keyed by month for each product
  const byMonth = (arr: MarketMonitoringItem[]) =>
    Object.fromEntries(arr.map((item) => [item.month, item]));

  const maps = {
    dam: byMonth(formattedDataArray.dam),
    rtm: byMonth(formattedDataArray.rtm),
    hpdam: byMonth(formattedDataArray.hpdam),
    gdam: byMonth(formattedDataArray.gdam),
    intraDay: byMonth(formattedDataArray.intraDay),
    contingencyContracts: byMonth(formattedDataArray.contingencyContracts),
    daily: byMonth(formattedDataArray.daily),
    weekly: byMonth(formattedDataArray.weekly),
    monthly: byMonth(formattedDataArray.monthly),
    anyDay: byMonth(formattedDataArray.anyDay),
    singleSided: byMonth(formattedDataArray.singleSided),
    bilateral: byMonth(formattedDataArray.bilateral),
  };

  const finalChartExchangeData: ChartExchangeItem[] = allMonths.map(
    (month) => ({
      month,
      dam: maps.dam[month]?.value ?? null,
      rtm: maps.rtm[month]?.value ?? null,
      hpdam: maps.hpdam[month]?.value ?? null,
      gdam: maps.gdam[month]?.value ?? null,
      intraDay: maps.intraDay[month]?.value ?? null,
      contingencyContracts: maps.contingencyContracts[month]?.value ?? null,
      daily: maps.daily[month]?.value ?? null,
      weekly: maps.weekly[month]?.value ?? null,
      monthly: maps.monthly[month]?.value ?? null,
      anyDay: maps.anyDay[month]?.value ?? null,
      singleSided: maps.singleSided[month]?.value ?? null,
      bilateral: maps.bilateral[month]?.value ?? null,
      dam_volume: maps.dam[month]?.totalVolume ?? null,
      rtm_volume: maps.rtm[month]?.totalVolume ?? null,
      hpdam_volume: maps.hpdam[month]?.totalVolume ?? null,
      gdam_volume: maps.gdam[month]?.totalVolume ?? null,
      intraDay_volume: maps.intraDay[month]?.totalVolume ?? null,
      contingencyContracts_volume:
        maps.contingencyContracts[month]?.totalVolume ?? null,
      daily_volume: maps.daily[month]?.totalVolume ?? null,
      weekly_volume: maps.weekly[month]?.totalVolume ?? null,
      monthly_volume: maps.monthly[month]?.totalVolume ?? null,
      anyDay_volume: maps.anyDay[month]?.totalVolume ?? null,
      singleSided_volume: maps.singleSided[month]?.totalVolume ?? null,
      bilateral_volume: maps.bilateral[month]?.totalVolume ?? null,
    }),
  );
  return finalChartExchangeData;
}

enum Exchange {
  IEX = "IEX",
  HPX = "HPX",
  PXIL = "PXIL",
  Traders = "Traders",
}

export interface ChartByPriceItem {
  month: string;
  iex?: number | null;
  hpx?: number | null;
  pxil?: number | null;
  traders?: number | null;
  [key: string]: number | string | null | undefined;
}

interface ByPriceItem {
  value: number;
  exchange: Exchange;
  month: string;
  cost?: number;
  totalVolume?: number;
}

interface ByPriceData {
  month: ByPriceItem[];
  iex: ByPriceItem[];
  hpx: ByPriceItem[];
  pxil: ByPriceItem[];
  traders: ByPriceItem[];
}

export function FormatByPriceData(
  data: any,
  selectedProduct?: String[],
  selectedMarket?: String[],
  isPrice?: boolean,
): ChartByPriceItem[] {
  const formattedDataArray: ByPriceData = {
    month: [],
    iex: [],
    hpx: [],
    pxil: [],
    traders: [],
  };

  const updateFormattedData = (
    dataArray: ByPriceItem[],
    exchange: Exchange,
    item: any,
  ) => {
    const index = dataArray.findIndex(
      (element) => element.month === item.month,
    );
    if (!selectedProduct?.includes(item.product)) {
      return;
    } else {
      if (index === -1) {
        var toBePushed: ByPriceItem = {
          month: item.month,
          exchange: exchange,
          value: parseFloat(item.value),
        };
        if (isPrice) {
          toBePushed.cost = item.value * item.volume;
          toBePushed.totalVolume = item.volume;
        }
        dataArray.push(toBePushed);
      } else {
        dataArray[index].value += parseFloat(item.value);
        if (isPrice) {
          dataArray[index].cost! += item.value * item.volume;
          dataArray[index].totalVolume! += item.volume;
          dataArray[index].value =
            dataArray[index].totalVolume === 0
              ? 0
              : dataArray[index].cost! / dataArray[index].totalVolume!;
        }
      }
    }
  };

  Object.keys(data).forEach((exchange) => {
    Object.keys(data[exchange]).forEach((market) => {
      if (selectedMarket?.includes(market)) {
        switch (exchange) {
          case Exchange.IEX:
            data[exchange][market].forEach((item: any) => {
              updateFormattedData(formattedDataArray.iex, Exchange.IEX, item);
            });
            break;
          case Exchange.HPX:
            data[exchange][market].forEach((item: any) => {
              updateFormattedData(formattedDataArray.hpx, Exchange.HPX, item);
            });
            break;
          case Exchange.PXIL:
            data[exchange][market].forEach((item: any) => {
              updateFormattedData(formattedDataArray.pxil, Exchange.PXIL, item);
            });
            break;
          case Exchange.Traders:
            data[exchange][market].forEach((item: any) => {
              updateFormattedData(
                formattedDataArray.traders,
                Exchange.Traders,
                item,
              );
            });
            break;
          default:
            break;
        }
      } 
    });
  });
  const MONTHS_BP = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const parseMonthOrderBP = (m: string) => {
    const [mon, yr] = m.split("-");
    return parseInt(yr) * 12 + MONTHS_BP.indexOf(mon);
  };

  const allMonthsBP = Array.from(
    new Set(
      (["iex", "hpx", "pxil", "traders"] as (keyof ByPriceData)[]).flatMap(
        (key) => formattedDataArray[key].map((item) => item.month),
      ),
    ),
  ).sort((a, b) => parseMonthOrderBP(a) - parseMonthOrderBP(b));

  const byMonthBP = (arr: ByPriceItem[]) =>
    Object.fromEntries(arr.map((item) => [item.month, item]));

  const mapsBP = {
    iex: byMonthBP(formattedDataArray.iex),
    hpx: byMonthBP(formattedDataArray.hpx),
    pxil: byMonthBP(formattedDataArray.pxil),
    traders: byMonthBP(formattedDataArray.traders),
  };

  const finalChartByPriceData: ChartByPriceItem[] = allMonthsBP.map(
    (month) => ({
      month,
      iex: mapsBP.iex[month]?.value ?? null,
      iex_volume: mapsBP.iex[month]?.totalVolume ?? null,
      hpx: mapsBP.hpx[month]?.value ?? null,
      hpx_volume: mapsBP.hpx[month]?.totalVolume ?? null,
      pxil: mapsBP.pxil[month]?.value ?? null,
      pxil_volume: mapsBP.pxil[month]?.totalVolume ?? null,
      traders: mapsBP.traders[month]?.value ?? null,
      traders_volume: mapsBP.traders[month]?.totalVolume ?? null,
    }),
  );
  return finalChartByPriceData;
}

export interface ComparativeSeriesMeta {
  dataKey: string;
  label: string;
  exchange: string;
  market: string;
  product: string;
}

export interface ComparativeByMarketProductResult {
  rows: ChartByPriceItem[];
  seriesMeta: ComparativeSeriesMeta[];
}

interface ComparativeAccumulator {
  value: number;
  totalVolume: number;
  totalCost: number;
}

export function FormatComparativeByMarketProductData(
  data: any,
  selectedProduct?: String[],
  selectedMarket?: String[],
  selectedExchange?: String[],
  isPrice?: boolean,
): ComparativeByMarketProductResult {
  const selectedProducts = selectedProduct
    ? new Set(selectedProduct.map((item) => String(item)))
    : null;
  const selectedMarkets = selectedMarket
    ? new Set(selectedMarket.map((item) => String(item)))
    : null;
  const selectedExchanges = selectedExchange
    ? new Set(selectedExchange.map((item) => String(item)))
    : null;

  const monthSet = new Set<string>();
  const seriesMetaMap = new Map<string, ComparativeSeriesMeta>();
  const usedSeriesKeys = new Set<string>();
  const combinationToSeriesKey = new Map<string, string>();
  const seriesBuckets: Record<
    string,
    Record<string, ComparativeAccumulator>
  > = {};

  Object.keys(data ?? {}).forEach((exchangeName) => {
    if (selectedExchanges && !selectedExchanges.has(exchangeName)) {
      return;
    }

    Object.keys(data[exchangeName] ?? {}).forEach((marketName) => {
      if (selectedMarkets && !selectedMarkets.has(marketName)) {
        return;
      }

      (data[exchangeName][marketName] ?? []).forEach((item: any) => {
        const itemProduct = String(item.product ?? "");
        if (selectedProducts && !selectedProducts.has(itemProduct)) {
          return;
        }

        const month = String(item.month ?? "");
        if (!month) {
          return;
        }

        const numericValue = Number(item.value);
        if (Number.isNaN(numericValue)) {
          return;
        }

        const volumeValue = Number(item.volume);
        const numericVolume = Number.isFinite(volumeValue) ? volumeValue : 0;

        const normalizedProduct = normalizeComparativeProduct(itemProduct);
        const label = buildComparativeLabel(
          exchangeName,
          marketName,
          normalizedProduct,
        );
        const combinationKey = `${exchangeName}|${marketName}|${normalizedProduct}`;
        const seriesKey =
          combinationToSeriesKey.get(combinationKey) ??
          getUniqueComparativeSeriesKey(
            exchangeName,
            marketName,
            normalizedProduct,
            usedSeriesKeys,
          );

        if (!combinationToSeriesKey.has(combinationKey)) {
          combinationToSeriesKey.set(combinationKey, seriesKey);
        }

        if (!seriesMetaMap.has(seriesKey)) {
          seriesMetaMap.set(seriesKey, {
            dataKey: seriesKey,
            label,
            exchange: exchangeName,
            market: marketName,
            product: normalizedProduct,
          });
        }

        monthSet.add(month);
        if (!seriesBuckets[seriesKey]) {
          seriesBuckets[seriesKey] = {};
        }

        if (!seriesBuckets[seriesKey][month]) {
          seriesBuckets[seriesKey][month] = {
            value: 0,
            totalVolume: 0,
            totalCost: 0,
          };
        }

        const accumulator = seriesBuckets[seriesKey][month];
        if (isPrice) {
          accumulator.totalCost += numericValue * numericVolume;
          accumulator.totalVolume += numericVolume;
          accumulator.value =
            accumulator.totalVolume === 0
              ? 0
              : accumulator.totalCost / accumulator.totalVolume;
        } else {
          accumulator.value += numericValue;
          accumulator.totalVolume += numericVolume;
        }
      });
    });
  });

  const sortedSeriesMeta = Array.from(seriesMetaMap.values()).sort((a, b) => {
    const byExchange = a.exchange.localeCompare(b.exchange);
    if (byExchange !== 0) {
      return byExchange;
    }

    const byMarket = a.market.localeCompare(b.market);
    if (byMarket !== 0) {
      return byMarket;
    }

    return a.product.localeCompare(b.product);
  });

  const sortedMonths = Array.from(monthSet).sort((a, b) => {
    return getMonthSortOrder(a) - getMonthSortOrder(b);
  });

  const rows: ChartByPriceItem[] = sortedMonths.map((month) => {
    const entry: ChartByPriceItem = { month };
    sortedSeriesMeta.forEach((series) => {
      const value = seriesBuckets[series.dataKey]?.[month];
      entry[series.dataKey] = value ? value.value : null;
      entry[`${series.dataKey}_volume`] = value ? value.totalVolume : null;
    });
    return entry;
  });

  return {
    rows,
    seriesMeta: sortedSeriesMeta,
  };
}

function normalizeComparativeProduct(product: string): string {
  const productMap: Record<string, string> = {
    "Intra-Day Contracts": "Intra Day",
    "Day Ahead Contingency Contracts": "DAC",
    "Daily Contracts": "Daily",
    "Weekly Contracts": "Weekly",
    "Monthly Contracts": "Monthly",
    "Any Day Single Sided Contracts": "Any Day S.S",
    "Trading License": "Bilateral",
    "Trading Licensees": "Bilateral",
  };

  return productMap[product] ?? product;
}

function buildComparativeLabel(
  exchangeName: string,
  marketName: string,
  productName: string,
): string {
  const parts = [exchangeName, marketName, productName]
    .map((part) => part.trim())
    .filter((part) => part.length > 0);

  const seen = new Set<string>();
  const deduped = parts.filter((part) => {
    const key = part.toLowerCase();
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });

  return deduped.join(" ");
}

function getMonthSortOrder(month: string): number {
  const monthOrder = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const [monthName, yearValue] = month.split("-");
  const year = Number(yearValue);
  const monthIndex = monthOrder.indexOf(monthName);

  if (!Number.isFinite(year) || monthIndex < 0) {
    return Number.MAX_SAFE_INTEGER;
  }

  return year * 12 + monthIndex;
}

function getUniqueComparativeSeriesKey(
  exchangeName: string,
  marketName: string,
  productName: string,
  usedSeriesKeys: Set<string>,
): string {
  const baseKey = `${slugifySeriesPart(exchangeName)}_${slugifySeriesPart(marketName)}_${slugifySeriesPart(productName)}`;

  if (!usedSeriesKeys.has(baseKey)) {
    usedSeriesKeys.add(baseKey);
    return baseKey;
  }

  let suffix = 2;
  let nextKey = `${baseKey}_${suffix}`;
  while (usedSeriesKeys.has(nextKey)) {
    suffix += 1;
    nextKey = `${baseKey}_${suffix}`;
  }

  usedSeriesKeys.add(nextKey);
  return nextKey;
}

function slugifySeriesPart(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/__+/g, "_");
}
